import Vue from "vue";

// 全局共享的 Loading 实例（用于非全屏且未指定挂载目标的情况）
let sharedLoading = null;
// 存储当前所有正在进行的共享 Loading 配置队列
let sharedLoadingConfigs = [];
// 共享 Loading 的唯一自增序列号（用于生成唯一 ID）
let sharedLoadingSeq = 0;
// 允许在共享 Loading 状态下动态更新的属性列表
const dynamicLoadingProps = ["background", "spinner", "customClass"];

/**
 * 判断是否应该使用全局共享的 Loading 实例
 * @param {Object} loaderConf - Loading 的配置对象
 * @returns {boolean} - 如果没有指定 target 且 fullscreen 为 false，则返回 true
 */
function isSharedLoading(loaderConf = {}) {
    return !loaderConf.target && loaderConf.fullscreen === false;
}

/**
 * 应用/更新共享 Loading 实例的显示配置
 * 注意：由于 Element UI 已将遮罩层追加到 DOM 中，因此与挂载相关的选项（如 target, body, fullscreen, lock）
 * 无法在创建后安全修改，此方法仅更新响应式的显示字段。
 * @param {Object} loaderConf - 最新的 Loading 配置对象
 */
function applySharedLoadingConfig(loaderConf = {}) {
    if (!sharedLoading) return;

    // 如果传入的文本发生变化，则更新 Loading 的文本内容
    if (loaderConf.text !== undefined && sharedLoading.text !== loaderConf.text) {
        sharedLoading.setText(loaderConf.text);
    }
    // 遍历允许动态更新的属性列表，若值发生变化则进行更新
    dynamicLoadingProps.forEach((prop) => {
        if (loaderConf[prop] !== undefined && sharedLoading[prop] !== loaderConf[prop]) {
            sharedLoading[prop] = loaderConf[prop];
        }
    });
}

/**
 * 关闭指定 ID 的共享 Loading 请求
 * 采用栈式管理：当某个请求结束时将其从队列中移除；若队列为空则彻底关闭 Loading；
 * 若队列中还有其他请求，则恢复最近一个请求的显示配置。
 * @param {number} id - 需要关闭的 Loading 的唯一标识符
 */
function closeSharedLoading(id) {
    // 从配置队列中过滤掉已结束的请求
    sharedLoadingConfigs = sharedLoadingConfigs.filter((item) => item.id !== id);

    // 如果队列已空，说明没有正在进行的共享请求，直接关闭并清空实例
    if (sharedLoadingConfigs.length === 0 && sharedLoading) {
        sharedLoading.close();
        sharedLoading = null;
        return;
    }

    // 获取队列中最新的一个有效配置
    const latest = sharedLoadingConfigs[sharedLoadingConfigs.length - 1];
    if (latest) {
        // 当最新的请求提前关闭时，恢复上一个活跃请求的显示配置
        applySharedLoadingConfig(latest.loaderConf);
    }
}

/**
 * 发起一个带 Loading 状态的请求
 * 根据配置决定是使用独立的 Loading 实例还是复用全局共享的 Loading 实例。
 * @param {Object} loaderConf - Loading 的配置参数
 * @returns {Object} - 包含 close() 方法的对象，调用 close() 可手动关闭当前的 Loading
 */
export function openRequestLoading(loaderConf) {
    // 如果不满足共享条件，则直接创建一个独立的 Loading 实例并返回
    if (!isSharedLoading(loaderConf)) {
        return Vue.prototype.$loading(loaderConf);
    }

    // 生成唯一的序列号，并将当前请求配置推入共享队列
    const id = ++sharedLoadingSeq;
    sharedLoadingConfigs.push({ id, loaderConf });

    // 如果当前不存在共享的 Loading 实例，则新建一个
    if (!sharedLoading) {
        sharedLoading = Vue.prototype.$loading(loaderConf);
    } else {
        // 如果已存在共享实例，则仅更新其显示配置
        applySharedLoadingConfig(loaderConf);
    }

    // 标志位，防止同一个 Loading 被重复关闭
    let closed = false;

    // 返回一个包含关闭方法的控制对象
    return {
        close() {
            if (closed) return;
            closed = true;
            // 执行关闭逻辑，移除对应 ID 的请求并处理后续状态
            closeSharedLoading(id);
        }
    };
}
